import { useEffect, useRef, useState } from "react";
import { osm } from "pigeon-maps/providers";

/** pigeon-maps tile function → `https://tile.openstreetmap.org/...` */
export const kioskMapTileProvider = osm;

export const kioskMapTileAttributionHtml =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Same tile space as pigeon-maps (`lng2tile` / `lat2tile`). */
const lng2tile = (lon, zoom) => ((lon + 180) / 360) * 2 ** zoom;

const lat2tile = (lat, zoom) => {
  const rad = (lat * Math.PI) / 180;
  return (
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom
  );
};

/**
 * Pixel width/height spanned by bounds at a given pigeon zoom (center irrelevant).
 */
function boundsPixelSpanAtZoom(bounds, zoom) {
  const [[swLat, swLng], [neLat, neLng]] = bounds;
  const south = Math.min(swLat, neLat);
  const north = Math.max(swLat, neLat);
  const west = Math.min(swLng, neLng);
  const east = Math.max(swLng, neLng);

  const tileW = Math.abs(lng2tile(east, zoom) - lng2tile(west, zoom)) * 256;
  const tileH = Math.abs(lat2tile(south, zoom) - lat2tile(north, zoom)) * 256;
  return { w: tileW, h: tileH };
}

/**
 * Fit [[southLat, westLng], [northLat, eastLng]] into a viewport using the same projection as pigeon-maps.
 * Uses fractional zoom so the view matches config/default zoom style and avoids floor() under-zoom.
 *
 * @param {number} paddingRatio — fraction of width/height to use (e.g. 0.92 leaves ~8% margin)
 */
export function fitLatLngBounds(
  bounds,
  mapWidth,
  mapHeight,
  minZoom = 1,
  maxZoom = 18,
  paddingRatio = 0.92
) {
  if (!bounds || !mapWidth || !mapHeight) {
    return null;
  }
  const [[swLat, swLng], [neLat, neLng]] = bounds;
  const south = Math.min(swLat, neLat);
  const north = Math.max(swLat, neLat);
  const west = Math.min(swLng, neLng);
  const east = Math.max(swLng, neLng);

  const center = [(south + north) / 2, (west + east) / 2];

  const innerW = Math.max(1, mapWidth * paddingRatio);
  const innerH = Math.max(1, mapHeight * paddingRatio);

  const fitsAt = (z) => {
    const { w, h } = boundsPixelSpanAtZoom(bounds, z);
    return w <= innerW && h <= innerH;
  };

  if (!fitsAt(minZoom)) {
    return { center, zoom: minZoom };
  }

  let lo = minZoom;
  let hi = maxZoom;
  for (let i = 0; i < 28; i++) {
    const mid = (lo + hi) / 2;
    if (fitsAt(mid)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const zoom = Math.min(maxZoom, Math.max(minZoom, lo));
  return { center, zoom };
}

/**
 * Scale HTML overlays with map zoom: one zoom step ≈ 2× geographic scale (Web Mercator / pigeon-maps).
 */
export function kioskMapOverlayScale(zoom, referenceZoom = 5.2) {
  const z = Number.isFinite(zoom) ? zoom : referenceZoom;
  return 2 ** (z - referenceZoom);
}

export function useKioskPigeonMapSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 800, height: 480 });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      const w = Math.max(1, Math.floor(r.width));
      const h = Math.max(1, Math.floor(r.height));
      setSize((prev) =>
        prev.width === w && prev.height === h ? prev : { width: w, height: h }
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
}
